import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";

const EMPTY = {
  name: "",
  price_monthly: 0,
  stripe_lookup_key: "",
  sort: 0,
  active: true,
  features: { max_forms: 1, max_submissions_month: 100, file_uploads: true, sms: false, custom_smtp: false },
};

export default function Plans() {
  const [plans, setPlans] = useState(null);
  const [editing, setEditing] = useState(null); // null | {plan, isNew}

  const load = () => api.get("/admin/plans").then(({ data }) => setPlans(data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    const { plan, isNew } = editing;
    try {
      if (isNew) await api.post("/admin/plans", plan);
      else await api.patch(`/admin/plans/${plan.id}`, plan);
      toast.success("Plan saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    try {
      await api.delete(`/admin/plans/${id}`);
      toast.success("Plan deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const p = editing?.plan;

  return (
    <div data-testid="admin-plans-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black">Plans</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Paid plans bill through Stripe using the lookup key (e.g. <code>pro_monthly</code>).
          </p>
        </div>
        <Button className="rounded-full" onClick={() => setEditing({ plan: { ...EMPTY, features: { ...EMPTY.features } }, isNew: true })} data-testid="create-plan-button">
          <Plus className="h-4 w-4 mr-1.5" /> New plan
        </Button>
      </div>

      <div className="mt-8 grid md:grid-cols-3 gap-4">
        {!plans ? (
          <p className="text-neutral-400">Loading…</p>
        ) : (
          plans.map((plan) => (
            <div key={plan.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid={`plan-card-${plan.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold">{plan.name}</h3>
                  <div className="text-2xl font-black font-heading mt-1">£{plan.price_monthly}<span className="text-sm font-normal text-neutral-500">/mo</span></div>
                </div>
                {!plan.active && <span className="text-xs bg-neutral-100 rounded-full px-2 py-0.5">inactive</span>}
              </div>
              <div className="mt-4 text-xs text-neutral-500 space-y-1">
                <div>Lookup key: <code>{plan.stripe_lookup_key || "—"}</code></div>
                <div>{plan.features?.max_forms} forms · {(plan.features?.max_submissions_month || 0).toLocaleString()} subs/mo</div>
                <div>{[plan.features?.file_uploads && "uploads", plan.features?.sms && "sms", plan.features?.custom_smtp && "custom smtp"].filter(Boolean).join(" · ")}</div>
              </div>
              <div className="mt-5 flex gap-2">
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => setEditing({ plan: JSON.parse(JSON.stringify(plan)), isNew: false })} data-testid={`plan-edit-${plan.id}`}>
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
                <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(plan.id)} data-testid={`plan-delete-${plan.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="plan-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.isNew ? "New plan" : "Edit plan"}</DialogTitle>
          </DialogHeader>
          {p && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={p.name} onChange={(e) => setEditing({ ...editing, plan: { ...p, name: e.target.value } })} data-testid="plan-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Price / month (GBP £)</Label>
                  <Input type="number" step="0.01" value={p.price_monthly} onChange={(e) => setEditing({ ...editing, plan: { ...p, price_monthly: parseFloat(e.target.value) || 0 } })} data-testid="plan-price-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Stripe lookup key</Label>
                  <Input value={p.stripe_lookup_key} onChange={(e) => setEditing({ ...editing, plan: { ...p, stripe_lookup_key: e.target.value } })} placeholder="pro_monthly" data-testid="plan-lookup-input" />
                </div>
                <div className="space-y-2">
                  <Label>Sort order</Label>
                  <Input type="number" value={p.sort} onChange={(e) => setEditing({ ...editing, plan: { ...p, sort: parseInt(e.target.value) || 0 } })} data-testid="plan-sort-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Max forms</Label>
                  <Input type="number" value={p.features.max_forms} onChange={(e) => setEditing({ ...editing, plan: { ...p, features: { ...p.features, max_forms: parseInt(e.target.value) || 0 } } })} data-testid="plan-max-forms-input" />
                </div>
                <div className="space-y-2">
                  <Label>Max submissions / month</Label>
                  <Input type="number" value={p.features.max_submissions_month} onChange={(e) => setEditing({ ...editing, plan: { ...p, features: { ...p.features, max_submissions_month: parseInt(e.target.value) || 0 } } })} data-testid="plan-max-subs-input" />
                </div>
              </div>
              <div className="flex flex-wrap gap-6">
                {[["file_uploads", "File uploads"], ["sms", "SMS"], ["custom_smtp", "Custom SMTP"], ["active", "Active"]].map(([k, label]) => (
                  <div key={k} className="flex items-center gap-2">
                    <Switch
                      checked={k === "active" ? p.active : !!p.features[k]}
                      onCheckedChange={(v) =>
                        k === "active"
                          ? setEditing({ ...editing, plan: { ...p, active: v } })
                          : setEditing({ ...editing, plan: { ...p, features: { ...p.features, [k]: v } } })
                      }
                      data-testid={`plan-switch-${k}`}
                    />
                    <Label>{label}</Label>
                  </div>
                ))}
              </div>
              <Button onClick={save} className="rounded-full w-full" data-testid="plan-save-button">Save plan</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
